import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faCashRegister } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../components/Button';
import { NumericKeypad } from '../../../../components/Keypad/NumericKeypad';
import keypadClasses from '../../../../components/Keypad/keypad-modal.module.scss';
import { appendDigit, appendDot, bufferToAmount, dropLastChar, round1 } from '../../../../components/Keypad/amount-buffer.utils';
import classes from './open-session-modal.module.scss';

const MAX_OPENING_CASH = 999999; // mirror the backend DTO bound (openingCash.max)

export type OpenSessionModalProps = {
  isOpen: boolean;
  mode: 'open' | 'edit';
  isManager: boolean;
  canOpenDrawer?: boolean;
  isSaving?: boolean;
  existingAmount?: number;
  onOpenDrawer: () => void;
  onConfirm: (openingCash: number) => void;
  onClose: () => void;
};

// Summary: Odoo opening-control parity modal — declares/edits the opening cash float (RTL).
const OpenSessionModalComponent: React.FC<OpenSessionModalProps> = ({
  isOpen,
  mode,
  isManager,
  canOpenDrawer = false,
  isSaving = false,
  existingAmount = 0,
  onOpenDrawer,
  onConfirm,
  onClose,
}) => {
  const [buffer, setBuffer] = useState<string>('');
  const replaceOnNextDigitRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      const seeded = existingAmount > 0 ? String(round1(existingAmount)) : '';
      setBuffer(seeded);
      // When we show a suggested amount, first typed digit should replace it.
      replaceOnNextDigitRef.current = seeded !== '';
    }
  }, [isOpen, mode, existingAmount]);

  if (!isOpen) return null;

  // Opening cash is optional; empty/'.' means 0 (a shift can open with no float).
  const numericValue = bufferToAmount(buffer);
  const isValid = Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= MAX_OPENING_CASH;

  const pressDigit = (digit: string) => {
    const shouldReplace = replaceOnNextDigitRef.current;
    replaceOnNextDigitRef.current = false;
    setBuffer((prev) => appendDigit(shouldReplace ? '' : prev, digit));
  };

  const pressDot = () => {
    replaceOnNextDigitRef.current = false;
    setBuffer((prev) => appendDot(prev));
  };

  const pressBackspace = () => {
    replaceOnNextDigitRef.current = false;
    setBuffer((prev) => dropLastChar(prev));
  };

  const handleConfirm = () => {
    if (!isValid || isSaving) return;
    onConfirm(numericValue);
  };

  const displayValue = buffer === '' ? '0' : buffer;
  const title = mode === 'edit' ? 'עדכון מזומן פתיחה' : 'פתיחת משמרת';
  const confirmLabel = mode === 'edit' ? 'עדכן מזומן פתיחה' : 'התחל משמרת';

  return (
    <div className={keypadClasses.overlay} role="dialog" aria-modal="true" aria-label={title} data-testid="session-open-modal">
      <div className={keypadClasses.modal}>
        <div className={keypadClasses.header}>
          <h2 className={keypadClasses.title}>{title}</h2>
        </div>

        <div className={keypadClasses.body}>
          <div className={keypadClasses.amountRow}>
            <div className={keypadClasses.amountCell}>
              <span className={keypadClasses.amountLabel}>מזומן פתיחה</span>
              <span className={keypadClasses.amountValue}>₪ {displayValue}</span>
            </div>
            {canOpenDrawer ? (
              <Button
                variant="save"
                size="sm"
                className={classes.drawerButton}
                onClick={onOpenDrawer}
                disabled={isSaving}
                dataTestId="session-open-drawer"
              >
                <FontAwesomeIcon icon={faCashRegister as IconProp} />
                <span>פתח מגירה</span>
              </Button>
            ) : (
              <span className={classes.drawerHint}>אין הרשאה לפתיחת מגירה</span>
            )}
          </div>

          <NumericKeypad
            dataTestId="session-open"
            keypadClassName={keypadClasses.keypad}
            keyClassName={keypadClasses.key}
            disabled={isSaving}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceDisabled={buffer === ''}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={{ label: '.', className: keypadClasses.key, onClick: pressDot, ariaLabel: 'נקודה עשרונית' }}
          />

          <div className={keypadClasses.actions}>
            <Button variant="cancel" fullWidth onClick={onClose} disabled={isSaving} dataTestId="session-open-cancel">
              ביטול
            </Button>
            <Button
              variant={mode === 'edit' ? 'save' : 'confirm'}
              fullWidth
              onClick={handleConfirm}
              disabled={!isValid || isSaving}
              loading={isSaving}
              loadingLabel="שומר..."
              dataTestId="session-open-confirm"
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const OpenSessionModal = React.memo(OpenSessionModalComponent);
